import HTMLParser from 'html-react-parser';
import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import {
	Box,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	IconButton,
	RadioGroup,
	Typography,
} from '@mui/material';

import { ApplicationFormSelectedQuestion } from '@pages/ApplicationForm/containers/ApplicationFormMain';
import useAgreementFieldApplicationForm from '@pages/ApplicationForm/containers/ApplicationFormMain/components/AgreementFieldApplicationForm/hooks';
import {
	AgreementBox,
	AgreementText,
} from '@pages/ApplicationForm/containers/ApplicationFormMain/components/AgreementFieldApplicationForm/styles';

function AgreementFieldApplicationForm({
	applicationId,
	question,
}: {
	applicationId: number;
	question: ApplicationFormSelectedQuestion;
}) {
	const {
		loading,
		handleSaveAnswer,
		handleDeleteAnswer,
		agreementTextFieldRef,
		handleScrollAgreementText,
		isReadAgreement,
	} = useAgreementFieldApplicationForm({
		applicationId,
		questionId: question.id,
		hasMultipleAnswers: question.multiple_answers,
	});

	const options = question.options.map(option => ({
		value: option.label,
		id: option.id,
		is_checked: option.is_checked,
		is_visible: option.is_visible,
		system_identifier: option.system_identifier,
	}));

	const disabled =
		loading ||
		question.is_locked ||
		Boolean(question.locked_for_applicants) ||
		(Boolean(question.locked_for_applicants_if_answered_by_non_applicant) && !question.is_self_answered);

	return (
		<Box mb={5}>
			<Grid container alignItems={'center'}>
				<Typography variant={'body2'} fontWeight={500}>
					Agreement
					{question.is_optional ? '' : ' *'}
				</Typography>

				<IconButton
					aria-label="delete answer"
					onClick={handleDeleteAnswer}
					disabled={!options.some(({ is_checked }) => is_checked) || disabled}
				>
					<DeleteIcon />
				</IconButton>
			</Grid>

			{question.description ? (
				<Box mb={2}>
					<Typography variant={'caption'} fontWeight={500}>
						{HTMLParser(question.description)}
					</Typography>
				</Box>
			) : null}

			<AgreementBox>
				<AgreementText ref={agreementTextFieldRef} onScroll={handleScrollAgreementText}>
					{question.label}
				</AgreementText>
			</AgreementBox>

			<FormControl error={question.errors && question.errors.length > 0}>
				<RadioGroup
					row
					aria-labelledby={`question-${question.id}-agreement-label`}
					name={`question-${question.id}-agreement`}
				>
					{options
						.filter(({ is_visible }) => is_visible)
						.map(option => (
							<FormControlLabel
								key={option.id}
								value={option.value}
								control={<Checkbox />}
								label={option.value}
								checked={option.is_checked}
								disabled={option.is_checked || !isReadAgreement || disabled}
								onChange={() => handleSaveAnswer(option.id)}
							/>
						))}
				</RadioGroup>

				<FormHelperText error={question.errors && question.errors.length > 0}>
					{question.errors.join('. ')}
				</FormHelperText>
			</FormControl>
		</Box>
	);
}

export default AgreementFieldApplicationForm;
